import React, { useState } from 'react';
import { useModal } from 'src/hooks/Modal';
import { FaArrowLeft, FaFilePdf } from 'react-icons/fa';
import { useToast } from 'src/hooks/Toast';
import api from 'src/services/api';
import { formatReal } from 'src/utils/format';


// ─────────────────────────────────────────────────────────────────────────────
//  2. SUBCOMPONENTE — inserir antes do FinanceReportComponent
// ─────────────────────────────────────────────────────────────────────────────

interface DevolucaoFormProps {
  projectHash: string;
  saldoAtual: number;     // centavos
  onSuccess: () => void;
  onClose: () => void;
}

  const C = {
  bg:        '#F4F6FA',
  surface:   '#FFFFFF',
  border:    '#E3E8F0',
  text:      '#1A2236',
  muted:     '#8693A8',
  blue:      '#1E3A5F',
  blueLight: '#EBF1FA',
  green:     '#0A7A55',
  greenBg:   '#E6F7F2',
  red:       '#C0392B',
  redBg:     '#FDECEA',
  amber:     '#92400E',   // ← usado no botão e no preview
  amberBg:   '#FEF3C7',  // ← usado no preview de confirmação
  purple:    '#5B21B6',
  purpleBg:  '#EDE9FE',
};

const DevolucaoForm = ({ projectHash, saldoAtual, onSuccess, onClose }: DevolucaoFormProps) => {
  const { addToast } = useToast();

  const [rawValue, setRawValue]   = useState('');
  const [observation, setObs]     = useState('');
  const [cnpj, setCnpj]           = useState('');
  const [razao_social, setRazao]  = useState('');
  const [phone, setPhone]         = useState('');
  const [email, setEmail]         = useState('');
  const [vencimento, setVenc]     = useState('2');   // padrão do backend
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [boletoUrl, setBoletoUrl] = useState<string | null>(null);

  // ── Parsers / máscaras ───────────────────────────────────────────────────

  const parseCents = (str: string): number => {
    const clean = str.replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '');
    return Math.round(parseFloat(clean || '0') * 100);
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (!digits) { setRawValue(''); return; }
    const cents      = parseInt(digits, 10);
    const reais      = (cents / 100).toFixed(2);
    const [int, dec] = reais.split('.');
    setRawValue(int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec);
    setError(null);
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 14);
    if (v.length > 12) v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
    else if (v.length > 8) v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4');
    else if (v.length > 5) v = v.replace(/^(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3');
    else if (v.length > 2) v = v.replace(/^(\d{2})(\d{0,3})/, '$1.$2');
    setCnpj(v);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 11);
    if (v.length > 10) v = v.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    else if (v.length > 6) v = v.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    else if (v.length > 2) v = v.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
    setPhone(v);
  };

  const valueInCents = parseCents(rawValue);

  // ── Validação ────────────────────────────────────────────────────────────

  const validate = (): string | null => {
    if (valueInCents <= 0)           return 'Informe um valor maior que zero.';
    
    if (!razao_social.trim())        return 'Razão social é obrigatória.';
    if (cnpj.replace(/\D/g, '').length !== 14) return 'CNPJ inválido.';
    if (!email.trim() || !email.includes('@')) return 'E-mail inválido.';
    if (phone.replace(/\D/g, '').length < 10)  return 'Telefone inválido.';
    const dias = parseInt(vencimento, 10);
    if (isNaN(dias) || dias < 1)     return 'Vencimento deve ser ao menos 1 dia.';
    return null;
  };

  // ── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }

    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(`/refund-to-global/${projectHash}`, {
        value:        valueInCents,
        observation:  observation.trim() || undefined,
        cnpj:         cnpj.replace(/\D/g, ''),
        razao_social: razao_social.trim(),
        phone:        phone.replace(/\D/g, ''),
        email:        email.trim(),
        vencimento:   parseInt(vencimento, 10),
      });

      if (data?.status === 'error') {
        setError(data?.message ?? 'Erro retornado pelo gateway de pagamento.');
        return;
      }

      if (data?.boleto_url) {
        setBoletoUrl(data.boleto_url);
        addToast({ title: `Boleto de ${formatReal(valueInCents)} gerado com sucesso.`, type: 'success' });
        onSuccess(); // atualiza o saldo no relatório mas mantém o modal aberto com o link
      } else {
        addToast({ title: `Devolução de ${formatReal(valueInCents)} realizada com sucesso.`, type: 'success' });
        onSuccess();
        onClose();
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Erro ao processar devolução. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers de estilo ────────────────────────────────────────────────────

  const inputBase: React.CSSProperties = {
    width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`,
    borderRadius: 8, fontSize: 14, color: C.text, background: C.surface,
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };

  const Field = ({
    label, required, children,
  }: { label: string; required?: boolean; children: React.ReactNode }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  // Tela de sucesso — exibida após o boleto ser gerado
  if (boletoUrl) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '4px 0', alignItems: 'center', textAlign: 'center' }}>

        {/* Ícone de sucesso */}
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 26 }}>✓</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>Boleto gerado com sucesso</span>
          <span style={{ fontSize: 13, color: C.muted }}>
            Valor: <strong style={{ color: C.text }}>{formatReal(valueInCents)}</strong> · Vencimento em <strong style={{ color: C.text }}>{vencimento} dia(s)</strong>
          </span>
        </div>

        {/* Link do boleto */}
        <a
          href={boletoUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', padding: '12px 20px', borderRadius: 8,
            background: C.blue, color: '#fff', fontWeight: 700, fontSize: 14,
            textDecoration: 'none',
          }}
        >
          <FaFilePdf size={14} /> Abrir boleto em PDF
        </a>

        {/* Copiar URL */}
        <button
          onClick={() => {
            navigator.clipboard.writeText(boletoUrl);
            addToast({ title: 'Link copiado!', type: 'info' });
          }}
          style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 18px', fontSize: 13, color: C.muted, fontWeight: 600, cursor: 'pointer', width: '100%' }}
        >
          Copiar link
        </button>

        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', fontSize: 13, color: C.muted, cursor: 'pointer', textDecoration: 'underline' }}
        >
          Fechar
        </button>

      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: '4px 0' }}>

      {/* Saldo de referência */}
      <div style={{ background: C.blueLight, borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.blue }}>Saldo atual do projeto</span>
        <span style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: saldoAtual >= 0 ? C.green : C.red }}>
          {saldoAtual < 0 ? `−${formatReal(Math.abs(saldoAtual))}` : formatReal(saldoAtual)}
        </span>
      </div>

      {/* Valor */}
      <Field label="Valor a devolver" required>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: C.muted, fontWeight: 600, pointerEvents: 'none' }}>
            R$
          </span>
          <input
            type="text" inputMode="numeric" placeholder="0,00"
            value={rawValue} onChange={handleValueChange} 
            style={{ ...inputBase, paddingLeft: 36, fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
          />
        </div>
      </Field>

      {/* Razão social + CNPJ lado a lado */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Razão Social" required>
          <input
            type="text" placeholder="Nome da empresa" value={razao_social}
            onChange={e => setRazao(e.target.value)} style={inputBase}
          />
        </Field>
        <Field label="CNPJ" required>
          <input
            type="text" inputMode="numeric" placeholder="00.000.000/0001-00"
            value={cnpj} onChange={handleCnpjChange} style={inputBase}
          />
        </Field>
      </div>

      {/* E-mail + Telefone lado a lado */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="E-mail" required>
          <input
            type="email" placeholder="contato@empresa.com.br"
            value={email} onChange={e => setEmail(e.target.value)} style={inputBase}
          />
        </Field>
        <Field label="Telefone" required>
          <input
            type="text" inputMode="numeric" placeholder="(51) 99999-9999"
            value={phone} onChange={handlePhoneChange} style={inputBase}
          />
        </Field>
      </div>

      {/* Vencimento + Observação lado a lado */}
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
        <Field label="Vencimento (dias)">
          <input
            type="number" min={1} max={60} placeholder="2"
            value={vencimento} onChange={e => setVenc(e.target.value)} style={inputBase}
          />
        </Field>
        <Field label="Observação">
          <input
            type="text" placeholder="Motivo da devolução…"
            value={observation} onChange={e => setObs(e.target.value)} style={inputBase}
          />
        </Field>
      </div>

      {/* Erro de validação */}
      {error && (
        <div style={{ background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.red, fontWeight: 600 }}>
          {error}
        </div>
      )}

      {/* Preview de confirmação */}
      {valueInCents > 0 && (
        <div style={{ background: C.amberBg, border: `1px solid #FCD34D`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.amber }}>
          Será gerado um boleto de <strong>{formatReal(valueInCents)}</strong> com vencimento em{' '}
          <strong>{vencimento || 2} dia(s)</strong> para <strong>{razao_social || '—'}</strong>.
        </div>
      )}

      {/* Botões */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button
          onClick={onClose} disabled={loading}
          style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.muted, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
        >
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading || valueInCents <= 0}
          style={{
            padding: '9px 22px', borderRadius: 8, border: 'none',
            background: loading || valueInCents <= 0 ? '#CBD5E1' : C.blue,
            color: '#fff', fontWeight: 700, fontSize: 13,
            cursor: loading || valueInCents <= 0 ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          {loading ? (
            <>
              <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.7s linear infinite' }} />
              Processando…
            </>
          ) : (
            <><FaArrowLeft size={12} /> Gerar boleto</>
          )}
        </button>
      </div>

    </div>
  );
};



export default DevolucaoForm;